import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Plus,
  Edit,
  Trash2,
  Calendar,
  Clock,
  Users,
  MapPin,
  CheckCircle,
  XCircle,
  AlertCircle,
  Eye,
  UserCheck,
  UserX,
  Mail,
  Phone,
  Loader2,
  Building2
} from 'lucide-react';
import { NoGymSelected } from '@/components/NoGymSelected';
import { gymApi } from '@/lib/api';
import { GymClass, ClassAttendee } from '@/types';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

const daysOfWeek = [
  { value: 0, label: 'Domingo' },
  { value: 1, label: 'Lunes' },
  { value: 2, label: 'Martes' },
  { value: 3, label: 'Miércoles' },
  { value: 4, label: 'Jueves' },
  { value: 5, label: 'Viernes' },
  { value: 6, label: 'Sábado' },
];

export default function ClassesPage() {
  const { gym, hasRole } = useAuth();
  const isSuperAdmin = hasRole(['super_admin']);
  const [classes, setClasses] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingClass, setEditingClass] = useState<GymClass | null>(null);
  const [isAttendeesDialogOpen, setIsAttendeesDialogOpen] = useState(false);
  const [selectedClassForAttendees, setSelectedClassForAttendees] = useState<GymClass | null>(null);
  const [attendees, setAttendees] = useState<ClassAttendee[]>([]);
  const [isEnrollDialogOpen, setIsEnrollDialogOpen] = useState(false);
  const [selectedClassForEnroll, setSelectedClassForEnroll] = useState<GymClass | null>(null);
  const [clients, setClients] = useState<any[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    instructor: '',
    day_of_week: 1,
    start_time: '09:00',
    end_time: '10:00',
    capacity: 15,
    room: '',
  });

  useEffect(() => {
    fetchClasses();
  }, [gym?.gym_id]);

  const fetchClasses = async () => {
    try {
      const gId = gym?.gym_id || undefined;
      const res = await gymApi.listClasses(null, gId);
      if (res.success) {
        setClasses(res.classes);
      }
    } catch (error) {
      console.error('Failed to fetch classes:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const canEdit = hasRole(['gym_admin', 'gym_supervisor']);
  const canEnroll = hasRole(['gym_admin', 'gym_supervisor', 'gym_staff']);
  const canViewAttendees = hasRole(['gym_admin', 'gym_supervisor', 'gym_staff']);

  const filteredClasses = selectedDay !== null
    ? classes.filter(c => Number(c.day_of_week) === selectedDay)
    : classes;

  const sortedClasses = [...filteredClasses].sort((a, b) => {
    const dayA = Number(a.day_of_week || 0);
    const dayB = Number(b.day_of_week || 0);
    if (dayA !== dayB) return dayA - dayB;

    const timeA = a.start_time || '00:00';
    const timeB = b.start_time || '00:00';
    return timeA.localeCompare(timeB);
  });

  const stats = {
    total: classes.length,
    active: classes.filter(c => c.status === 'active').length,
    full: classes.filter(c => c.status === 'full').length,
    cancelled: classes.filter(c => c.status === 'cancelled').length,
    totalCapacity: classes.reduce((sum, c) => sum + Number(c.capacity), 0),
    totalEnrolled: classes.reduce((sum, c) => sum + Number(c.enrolled || 0), 0),
  };

  const handleOpenDialog = (gymClass?: GymClass) => {
    if (gymClass) {
      setEditingClass(gymClass);
      setFormData({
        name: gymClass.name,
        description: gymClass.description,
        instructor: gymClass.instructor,
        day_of_week: Number(gymClass.day_of_week),
        start_time: gymClass.start_time,
        end_time: gymClass.end_time,
        capacity: Number(gymClass.capacity),
        room: gymClass.room || '',
      });
    } else {
      setEditingClass(null);
      setFormData({
        name: '',
        description: '',
        instructor: '',
        day_of_week: 1,
        start_time: '09:00',
        end_time: '10:00',
        capacity: 15,
        room: '',
      });
    }
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      if (editingClass) {
        const res = await gymApi.updateClass(
          editingClass.class_id,
          { ...formData, status: editingClass.status },
          null,
          gym?.gym_id || undefined
        );
        if (res.success) {
          await fetchClasses();
          toast({
            title: 'Clase actualizada',
            description: `${formData.name} ha sido modificada exitosamente`,
          });
        }
      } else {
        const res = await gymApi.createClass(formData, null, gym?.gym_id || undefined);
        if (res.success) {
          await fetchClasses();
          toast({
            title: 'Clase creada',
            description: `${formData.name} ha sido programada exitosamente`,
          });
        }
      }
      setIsDialogOpen(false);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo guardar la clase',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleStatus = async (classId: string) => {
    const cls = classes.find(c => c.class_id === classId);
    if (!cls) return;

    const newStatus = cls.status === 'cancelled' ? 'active' : 'cancelled';

    try {
      const res = await gymApi.updateClassStatus(classId, newStatus, null, gym?.gym_id || undefined);
      if (res.success) {
        await fetchClasses();
        toast({
          title: newStatus === 'active' ? 'Clase reactivada' : 'Clase cancelada',
          description: `El estado de la clase ha sido actualizado a ${newStatus === 'active' ? 'activa' : 'cancelada'}.`,
        });
      }
    } catch (error) {
      console.error('Failed to toggle class status:', error);
      toast({
        title: 'Error',
        description: 'No se pudo cambiar el estado de la clase.',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteClass = async (classId: string) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar esta clase? Esta acción no se puede deshacer.')) return;

    try {
      const res = await gymApi.deleteClass(classId, null, gym?.gym_id || undefined);
      if (res.success) {
        await fetchClasses();
        toast({
          title: 'Clase eliminada',
          description: 'La clase ha sido eliminada exitosamente.',
        });
      }
    } catch (error) {
      console.error('Failed to delete class:', error);
      toast({
        title: 'Error',
        description: 'No se pudo eliminar la clase.',
        variant: 'destructive',
      });
    }
  };

  const handleEnroll = async (gymClass: GymClass) => {
    setSelectedClassForEnroll(gymClass);
    setIsEnrollDialogOpen(true);

    // Fetch clients if not already fetched
    if (clients.length === 0) {
      try {
        const gId = gym?.gym_id || undefined;
        const res = await gymApi.listClients(null, gId);
        if (res.success) {
          setClients(res.clients);
        }
      } catch (error) {
        console.error('Failed to fetch clients:', error);
      }
    }
  };

  const handleEnrollSubmit = async () => {
    if (!selectedClassForEnroll || !selectedClientId) return;

    setIsSubmitting(true);
    try {
      const res = await gymApi.enrollInClass(
        selectedClassForEnroll.class_id,
        selectedClientId,
        null,
        gym?.gym_id || undefined
      );

      if (res.success) {
        toast({
          title: 'Inscripción exitosa',
          description: 'El miembro ha sido inscrito a la clase.',
        });
        setIsEnrollDialogOpen(false);
        setSelectedClientId('');
        fetchClasses(); // Refresh counts
      } else {
        toast({
          title: 'Error',
          description: res.error || 'No se pudo realizar la inscripción',
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Error crítico al procesar la inscripción',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleViewAttendees = async (gymClass: GymClass) => {
    setSelectedClassForAttendees(gymClass);
    setAttendees([]);
    setIsAttendeesDialogOpen(true);

    try {
      const res = await gymApi.listClassAttendees(null, gymClass.class_id, gym?.gym_id || undefined);
      if (res.success) {
        setAttendees(res.attendees);
      }
    } catch (error) {
      console.error('Failed to fetch attendees:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los inscritos',
        variant: 'destructive',
      });
    }
  };

  const getAttendeeStatusBadge = (status: ClassAttendee['status']) => {
    switch (status) {
      case 'confirmed':
        return <Badge variant="info"><UserCheck className="h-3 w-3 mr-1" />Confirmado</Badge>;
      case 'attended':
        return <Badge variant="success"><CheckCircle className="h-3 w-3 mr-1" />Asistió</Badge>;
      case 'no_show':
        return <Badge variant="destructive"><UserX className="h-3 w-3 mr-1" />No asistió</Badge>;
    }
  };

  const getStatusBadge = (status: GymClass['status']) => {
    switch (status) {
      case 'active':
        return <Badge variant="success"><CheckCircle className="h-3 w-3 mr-1" />Activa</Badge>;
      case 'full':
        return <Badge variant="warning"><AlertCircle className="h-3 w-3 mr-1" />Llena</Badge>;
      case 'cancelled':
        return <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" />Cancelada</Badge>;
      default:
        return <Badge variant="secondary">{status || 'Activa'}</Badge>;
    }
  };

  const getDayLabel = (dayNum: number) => daysOfWeek.find(d => d.value === dayNum)?.label || '';

  if (isLoading) {
    return (
      <DashboardLayout title="Clases">
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title="Clases"
      subtitle={gym ? `Gestión de clases grupales y horarios de ${gym.name}` : "Gestión global de clases"}
      actions={
        canEdit && (
          <Button onClick={() => handleOpenDialog()} variant="gradient" disabled={!gym}>
            <Plus className="h-4 w-4 mr-2" />
            Nueva Clase
          </Button>
        )
      }
    >
      {isSuperAdmin && !gym && <NoGymSelected />}

      <div className="space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="glass-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Calendar className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total}</p>
                  <p className="text-xs text-muted-foreground">Total Clases</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="glass-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-500/10">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.active}</p>
                  <p className="text-xs text-muted-foreground">Activas</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="glass-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-yellow-500/10">
                  <Users className="h-5 w-5 text-yellow-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.totalEnrolled}</p>
                  <p className="text-xs text-muted-foreground">Inscritos</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="glass-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Users className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.totalCapacity}</p>
                  <p className="text-xs text-muted-foreground">Capacidad Total</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Day Filter */}
        <Card className="glass-card">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-2">
              <Button
                variant={selectedDay === null ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedDay(null)}
              >
                Todos
              </Button>
              {daysOfWeek.map(day => (
                <Button
                  key={day.value}
                  variant={selectedDay === day.value ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedDay(day.value)}
                >
                  {day.label}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Classes Grid */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {sortedClasses.map((gymClass) => (
            <Card
              key={gymClass.class_id}
              className={cn(
                "glass-card transition-all hover:shadow-lg",
                gymClass.status === 'cancelled' && "opacity-60"
              )}
            >
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-lg">{gymClass.name}</CardTitle>
                    <p className="text-sm text-muted-foreground">{gymClass.instructor}</p>
                  </div>
                  {getStatusBadge(gymClass.status)}
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground line-clamp-2">
                  {gymClass.description}
                </p>

                <div className="flex flex-wrap gap-3 text-sm">
                  <div className="flex items-center gap-1 text-muted-foreground">
                    <Calendar className="h-4 w-4" />
                    {getDayLabel(Number(gymClass.day_of_week))}
                  </div>
                  <div className="flex items-center gap-1 text-muted-foreground">
                    <Clock className="h-4 w-4" />
                    {gymClass.start_time} - {gymClass.end_time}
                  </div>
                </div>

                <div className="flex flex-wrap gap-3 text-sm">
                  <div className="flex items-center gap-1 text-muted-foreground">
                    <Users className="h-4 w-4" />
                    {gymClass.enrolled || 0}/{gymClass.capacity}
                  </div>
                  {gymClass.room && (
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <MapPin className="h-4 w-4" />
                      {gymClass.room}
                    </div>
                  )}
                </div>

                {/* Progress bar */}
                <div className="w-full bg-muted rounded-full h-2">
                  <div
                    className={cn(
                      "h-2 rounded-full transition-all",
                      (gymClass.enrolled || 0) >= gymClass.capacity ? "bg-yellow-500" : "bg-primary"
                    )}
                    style={{ width: `${Math.min(((gymClass.enrolled || 0) / gymClass.capacity) * 100, 100)}%` }}
                  />
                </div>

                {isSuperAdmin && !gym && (
                  <div className="flex items-center gap-2 pt-1 text-muted-foreground border-t border-border/10">
                    <Building2 className="h-3 w-3" />
                    <span className="text-xs font-semibold uppercase tracking-wider">
                      ID: {gymClass.gym_id?.substring(0, 8)}...
                    </span>
                  </div>
                )}

                {/* Actions */}
                <div className="flex flex-col gap-2 pt-3 border-t border-border/10">
                  <div className="flex flex-wrap gap-2">
                    {canEnroll && gymClass.status === 'active' && (gymClass.enrolled || 0) < gymClass.capacity && (
                      <Button
                        size="sm"
                        onClick={() => handleEnroll(gymClass)}
                        variant="gradient"
                        className="flex-1"
                      >
                        <Users className="h-3.5 w-3.5 mr-1.5" />
                        Inscribir
                      </Button>
                    )}
                    {canViewAttendees && (gymClass.enrolled || 0) > 0 && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleViewAttendees(gymClass)}
                        className="flex-1"
                      >
                        <Eye className="h-3.5 w-3.5 mr-1.5" />
                        Inscritos
                      </Button>
                    )}
                  </div>

                  {canEdit && (
                    <div className="flex items-center justify-end gap-1.5 mt-1 pt-2 border-t border-dashed border-border/20">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 rounded-full hover:bg-muted"
                        onClick={() => handleToggleStatus(gymClass.class_id)}
                        title={gymClass.status === 'cancelled' ? 'Activar Clase' : 'Cancelar Clase'}
                      >
                        {gymClass.status === 'cancelled' ? (
                          <CheckCircle className="h-4 w-4 text-success" />
                        ) : (
                          <XCircle className="h-4 w-4 text-destructive" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 rounded-full hover:bg-muted"
                        onClick={() => handleOpenDialog(gymClass)}
                        title="Editar Clase"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 rounded-full hover:bg-destructive/10 text-destructive"
                        onClick={() => handleDeleteClass(gymClass.class_id)}
                        title="Eliminar Clase"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}

          {sortedClasses.length === 0 && (
            <Card className="glass-card col-span-full">
              <CardContent className="py-12 text-center text-muted-foreground">
                No hay clases programadas {selectedDay !== null && `para ${getDayLabel(selectedDay)}`}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{editingClass ? 'Editar Clase' : 'Crear Nueva Clase'}</DialogTitle>
            <DialogDescription>
              {editingClass ? 'Modifica los detalles de la sesión.' : 'Completa la información para programar una nueva clase.'}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nombre de la Clase *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Ej: Spinning, Yoga, CrossFit"
                disabled={isSubmitting}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Descripción</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Descripción de la clase..."
                rows={2}
                disabled={isSubmitting}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="instructor">Instructor *</Label>
                <Input
                  id="instructor"
                  value={formData.instructor}
                  onChange={(e) => setFormData(prev => ({ ...prev, instructor: e.target.value }))}
                  placeholder="Nombre del instructor"
                  disabled={isSubmitting}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="room">Sala</Label>
                <Input
                  id="room"
                  value={formData.room}
                  onChange={(e) => setFormData(prev => ({ ...prev, room: e.target.value }))}
                  placeholder="Ej: Sala Principal"
                  disabled={isSubmitting}
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Día *</Label>
                <Select
                  value={formData.day_of_week.toString()}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, day_of_week: parseInt(value) }))}
                  disabled={isSubmitting}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {daysOfWeek.map(day => (
                      <SelectItem key={day.value} value={day.value.toString()}>
                        {day.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="start_time">Inicio</Label>
                <Input
                  id="start_time"
                  type="time"
                  value={formData.start_time}
                  onChange={(e) => setFormData(prev => ({ ...prev, start_time: e.target.value }))}
                  disabled={isSubmitting}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end_time">Fin</Label>
                <Input
                  id="end_time"
                  type="time"
                  value={formData.end_time}
                  onChange={(e) => setFormData(prev => ({ ...prev, end_time: e.target.value }))}
                  disabled={isSubmitting}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="capacity">Capacidad Máxima</Label>
              <Input
                id="capacity"
                type="number"
                min="1"
                value={formData.capacity}
                onChange={(e) => setFormData(prev => ({ ...prev, capacity: parseInt(e.target.value) || 1 }))}
                disabled={isSubmitting}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button onClick={handleSave} variant="gradient" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Guardando...
                </>
              ) : (
                editingClass ? 'Guardar Cambios' : 'Crear Clase'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Enrollment Dialog */}
      <Dialog open={isEnrollDialogOpen} onOpenChange={setIsEnrollDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Inscribir Miembro</DialogTitle>
            <DialogDescription>
              Selecciona un miembro para inscribirlo en: <span className="font-semibold text-foreground">{selectedClassForEnroll?.name}</span>
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="client">Miembro *</Label>
              <Select
                value={selectedClientId}
                onValueChange={setSelectedClientId}
                disabled={isSubmitting}
              >
                <SelectTrigger id="client">
                  <SelectValue placeholder="Seleccionar miembro..." />
                </SelectTrigger>
                <SelectContent>
                  {clients.length === 0 && (
                    <div className="p-4 text-sm text-center text-muted-foreground">
                      No se encontraron miembros. Asegúrate de haber creado miembros en la pestaña de <strong>Clientes</strong>.
                    </div>
                  )}
                  {clients
                    .filter(c => c.status?.toLowerCase() === 'active')
                    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                    .map(client => (
                      <SelectItem key={client.client_id} value={client.client_id}>
                        {client.name} ({client.phone || 'Sin teléfono'})
                      </SelectItem>
                    ))}
                  {clients.length > 0 && clients.filter(c => c.status?.toLowerCase() === 'active').length === 0 && (
                    <div className="p-4 text-sm text-center text-muted-foreground">
                      No hay miembros con estado <strong>Activo</strong>.
                    </div>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEnrollDialogOpen(false)} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button
              onClick={handleEnrollSubmit}
              variant="gradient"
              disabled={isSubmitting || !selectedClientId}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Inscribiendo...
                </>
              ) : (
                'Confirmar Inscripción'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Attendees Dialog (Read-Only) */}
      <Dialog open={isAttendeesDialogOpen} onOpenChange={setIsAttendeesDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Inscritos: {selectedClassForAttendees?.name}
            </DialogTitle>
            <DialogDescription>
              {selectedClassForAttendees && (
                <>
                  {getDayLabel(Number(selectedClassForAttendees.day_of_week))} • {selectedClassForAttendees.start_time} - {selectedClassForAttendees.end_time} • {selectedClassForAttendees.instructor}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {attendees.length > 0 ? (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Contacto</TableHead>
                      <TableHead>Estado</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {attendees.map((attendee) => (
                      <TableRow key={attendee.client_id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{attendee.client_name}</p>
                            <p className="text-xs text-muted-foreground">
                              Inscrito: {attendee.enrolled_at}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Mail className="h-3 w-3" />
                              {attendee.client_email}
                            </div>
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Phone className="h-3 w-3" />
                              {attendee.client_phone}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {getAttendeeStatusBadge(attendee.status)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="py-8 text-center text-muted-foreground">
                <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No hay inscritos en esta clase</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <div className="flex justify-between w-full items-center">
              <p className="text-sm text-muted-foreground">
                {attendees.length} inscrito{attendees.length !== 1 ? 's' : ''} de {selectedClassForAttendees?.capacity} cupos
              </p>
              <Button variant="outline" onClick={() => setIsAttendeesDialogOpen(false)}>
                Cerrar
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}